import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { quotesService } from '../../services/quotesService';
import { clientsService } from '../../services/clientsService';
import { uploadService } from '../../services/uploadService';
import './QuoteBuilder.css';
import './components/ComponentForms.css';

// Import component type forms
import CruiseForm from './components/CruiseForm';
import FlightForm from './components/FlightForm';
import HotelForm from './components/HotelForm';
import CarRentalForm from './components/CarRentalForm';
import RestaurantForm from './components/RestaurantForm';
import PackageDealForm from './components/PackageDealForm';
import GeneralItemForm from './components/GeneralItemForm';
import InsuranceForm from './components/InsuranceForm';
import ClientModal from './ClientModal';

const QuoteBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // Get quote ID from URL for editing
  const isEditMode = !!id;

  // Quote state
  const [quote, setQuote] = useState({
    quoteTitle: '',
    client: null,
    travelDates: {
      start: '',
      end: ''
    },
    travelers: {
      adults: 1,
      children: []
    },
    components: [],
    options: [], // For package comparisons
    totalPrice: 0,
    depositAmount: 0,
    depositDueDate: '',
    notes: '',
    status: 'draft',
    agentInfo: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      agencyName: user?.agencyName || ''
    }
  });

  // UI state
  const [activeStep, setActiveStep] = useState('client'); // client, details, components, preview
  const [showClientModal, setShowClientModal] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [selectedComponentType, setSelectedComponentType] = useState('');
  const [editingComponent, setEditingComponent] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [errors, setErrors] = useState({});

  // Component types
  const componentTypes = [
    { id: 'cruise', name: 'Cruise', icon: '🚢' },
    { id: 'flight', name: 'Flight', icon: '✈️' },
    { id: 'hotel', name: 'Hotel', icon: '🏨' },
    { id: 'car', name: 'Car Rental', icon: '🚗' },
    { id: 'restaurant', name: 'Restaurant', icon: '🍽️' },
    { id: 'package', name: 'Package Deal', icon: '📦' },
    { id: 'general', name: 'General Item', icon: '📋' },
    { id: 'insurance', name: 'Travel Insurance', icon: '🛡️' }
  ];

  // Load clients on mount
  useEffect(() => {
    fetchClients();
    if (isEditMode) {
      fetchQuote();
    }
  }, [id, isEditMode]);

  const fetchClients = async () => {
    try {
      const clients = await clientsService.getAll();
      setClients(clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setErrors({ fetch: 'Failed to load clients' });
    }
  };

  const fetchQuote = async () => {
    try {
      setLoadingQuote(true);
      const quoteData = await quotesService.getById(id);
      
      console.log('Loaded quote data:', quoteData); // Debug log
      
      // Parse JSON strings if they come as strings
      let componentsArray = [];
      let optionsArray = [];
      
      try {
        // Check if components is a string and try to parse it
        if (typeof quoteData.components === 'string') {
          componentsArray = JSON.parse(quoteData.components) || [];
        } else if (Array.isArray(quoteData.components)) {
          componentsArray = quoteData.components;
        }
        
        // Check if options is a string and try to parse it
        if (typeof quoteData.options === 'string') {
          optionsArray = JSON.parse(quoteData.options) || [];
        } else if (Array.isArray(quoteData.options)) {
          optionsArray = quoteData.options;
        }
      } catch (parseError) {
        console.error('Error parsing JSON data:', parseError);
        // Set defaults if parsing fails
        componentsArray = [];
        optionsArray = [];
      }
      
      // Transform the quote data from API to component format
      const transformedQuote = {
        quoteTitle: quoteData.title || '',
        client: {
          id: quoteData.client_id,
          firstName: quoteData.client_name?.split(' ')[0] || '',
          lastName: quoteData.client_name?.split(' ').slice(1).join(' ') || '',
          email: quoteData.client_email || '',
          phone: quoteData.client_phone || ''
        },
        travelDates: {
          start: quoteData.travel_start_date ? quoteData.travel_start_date.split('T')[0] : '',
          end: quoteData.travel_end_date ? quoteData.travel_end_date.split('T')[0] : ''
        },
        travelers: {
          adults: quoteData.adults || 1,
          children: Array(quoteData.children || 0).fill({ age: 0 })
        },
        components: componentsArray,
        options: optionsArray,
        totalPrice: parseFloat(quoteData.total_amount) || 0,
        depositAmount: parseFloat(quoteData.deposit_amount) || 0,
        depositDueDate: quoteData.deposit_due_date ? quoteData.deposit_due_date.split('T')[0] : '',
        notes: quoteData.notes || '',
        status: quoteData.status || 'draft',
        agentInfo: {
          name: user?.name || '',
          email: user?.email || '',
          phone: user?.phone || '',
          agencyName: user?.agencyName || ''
        }
      };
      
      setQuote(transformedQuote);
      
      // If we have data, show the preview step instead of client step
      if (transformedQuote.client && transformedQuote.client.id) {
        setActiveStep('preview');
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
      setErrors({ fetch: 'Failed to load quote' });
      alert('Failed to load quote. Please try again.');
      navigate('/agent/quotes');
    } finally {
      setLoadingQuote(false);
    }
  };

  // Calculate total price when components change
  useEffect(() => {
    try {
      // Make sure components and options are arrays
      const components = Array.isArray(quote.components) ? quote.components : [];
      const options = Array.isArray(quote.options) ? quote.options : [];
      
      // Calculate total from components
      const total = components.reduce((sum, component) => {
        const price = parseFloat(component.price) || 0;
        return sum + price;
      }, 0);
      
      // Calculate total from options
      const optionsTotal = options.reduce((sum, option) => {
        // Make sure option.components is an array
        const optComponents = Array.isArray(option.components) ? option.components : [];
        
        const optionSum = optComponents.reduce((optSum, comp) => {
          const price = parseFloat(comp.price) || 0;
          return optSum + price;
        }, 0);
        
        return sum + optionSum;
      }, 0);
      
      // Update quote with calculated total
      setQuote(prev => ({ ...prev, totalPrice: total + optionsTotal }));
    } catch (error) {
      console.error('Error calculating total price:', error);
      // Keep existing total price if calculation fails
    }
  }, [quote.components, quote.options]);

  // Handle client selection
  const handleClientSelect = (client) => {
    // Normalize client data structure
    const normalizedClient = {
      ...client,
      firstName: client.firstName || client.first_name,
      lastName: client.lastName || client.last_name
    };
    setQuote(prev => ({ ...prev, client: normalizedClient }));
    setShowClientModal(false);
    setActiveStep('details');
  };

  // Handle new client creation
  const handleNewClient = async (clientData) => {
    try {
      setLoading(true);
      const newClient = await clientsService.create(clientData);
      setClients(prev => [...prev, newClient]);
      handleClientSelect(newClient);
    } catch (error) {
      console.error('Error creating client:', error);
      setErrors({ client: 'Failed to create client' });
    } finally {
      setLoading(false);
    }
  };

  // Handle component addition
  const handleAddComponent = (componentData) => {
    const newComponent = {
      id: editingComponent?.id || Date.now(),
      type: selectedComponentType,
      ...componentData,
      createdAt: editingComponent?.createdAt || new Date().toISOString()
    };

    if (editingComponent) {
      setQuote(prev => ({
        ...prev,
        components: prev.components.map(comp => 
          comp.id === editingComponent.id ? newComponent : comp
        )
      }));
      setEditingComponent(null);
    } else {
      setQuote(prev => ({
        ...prev,
        components: [...prev.components, newComponent]
      }));
    }

    setShowComponentModal(false);
    setSelectedComponentType('');
  };

  // Handle component deletion
  const handleDeleteComponent = (componentId) => {
    setQuote(prev => ({
      ...prev,
      components: prev.components.filter(comp => comp.id !== componentId)
    }));
  };

  // Handle quote save
  const handleSaveQuote = async (sendToClient = false) => {
    setLoading(true);
    setErrors({});
    
    try {
      // Prepare quote data for API
      const quoteData = {
        title: quote.quoteTitle,
        client_id: quote.client?.id || null,
        client_name: `${quote.client?.firstName || ''} ${quote.client?.lastName || ''}`.trim(),
        client_email: quote.client?.email || '',
        client_phone: quote.client?.phone || '',
        travel_start_date: quote.travelDates.start,
        travel_end_date: quote.travelDates.end,
        adults: quote.travelers.adults,
        children: quote.travelers.children.length,
        components: JSON.stringify(quote.components),
        options: JSON.stringify(quote.options),
        total_amount: quote.totalPrice,
        deposit_amount: quote.depositAmount || 0,
        deposit_due_date: quote.depositDueDate || null,
        notes: quote.notes || '',
        status: sendToClient ? 'sent' : 'draft'
      };

      let response;
      if (isEditMode) {
        try {
          response = await quotesService.update(id, quoteData);
        } catch (updateError) {
          // If we get an error possibly related to missing notes column
          if (updateError.response?.status === 400 || 
              updateError.message?.includes('notes') || 
              updateError.message?.includes('column')) {
            console.warn('Possible database schema issue with notes field, retrying without notes');
            // Try again without the notes field
            const { notes, ...quoteDataWithoutNotes } = quoteData;
            response = await quotesService.update(id, quoteDataWithoutNotes);
          } else {
            // Re-throw if it's some other error
            throw updateError;
          }
        }
      } else {
        try {
          response = await quotesService.create(quoteData);
        } catch (createError) {
          // If we get an error possibly related to missing notes column
          if (createError.response?.status === 400 || 
              createError.message?.includes('notes') || 
              createError.message?.includes('column')) {
            console.warn('Possible database schema issue with notes field, retrying without notes');
            // Try again without the notes field
            const { notes, ...quoteDataWithoutNotes } = quoteData;
            response = await quotesService.create(quoteDataWithoutNotes);
          } else {
            // Re-throw if it's some other error
            throw createError;
          }
        }
      }
      
      if (sendToClient && (response.quoteId || response.id || id)) {
        // Send email to client
        await quotesService.send(response.quoteId || response.id || id, {});
        // Show success message
        alert('Quote sent successfully to ' + quote.client?.email);
      } else if (!sendToClient) {
        // Show success message for save
        alert(isEditMode ? 'Quote updated successfully!' : 'Quote saved as draft!');
      }

      // Navigate back to quotes list
      navigate('/agent/quotes');
    } catch (error) {
      console.error('Error saving quote:', error);
      let errorMessage = 'Failed to save quote. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check if we should handle schema issue even in the outer catch
      if ((error.response?.status === 400 || 
          error.message?.includes('notes') || 
          error.message?.includes('column')) && 
          quote.notes) {
        
        console.warn('Attempting last-chance recovery for possible schema issue');
        try {
          // Final attempt without notes field
          const { notes, ...quoteDataWithoutNotes } = {
            title: quote.quoteTitle,
            client_id: quote.client?.id || null,
            client_name: `${quote.client?.firstName || ''} ${quote.client?.lastName || ''}`.trim(),
            client_email: quote.client?.email || '',
            client_phone: quote.client?.phone || '',
            travel_start_date: quote.travelDates.start,
            travel_end_date: quote.travelDates.end,
            adults: quote.travelers.adults,
            children: quote.travelers.children.length,
            components: JSON.stringify(quote.components),
            options: JSON.stringify(quote.options),
            total_amount: quote.totalPrice,
            deposit_amount: quote.depositAmount || 0,
            deposit_due_date: quote.depositDueDate || null,
            status: sendToClient ? 'sent' : 'draft'
          };
          
          const response = isEditMode ? 
            await quotesService.update(id, quoteDataWithoutNotes) : 
            await quotesService.create(quoteDataWithoutNotes);
            
          if (sendToClient && (response.quoteId || response.id || id)) {
            await quotesService.send(response.quoteId || response.id || id, {});
            alert('Quote sent successfully to ' + quote.client?.email);
          } else if (!sendToClient) {
            alert(isEditMode ? 'Quote updated successfully!' : 'Quote saved as draft!');
          }
          
          navigate('/agent/quotes');
          return; // Exit early since we recovered
        } catch (recoveryError) {
          console.error('Recovery attempt failed:', recoveryError);
          // Continue to normal error handling
        }
      }
      
      setErrors({ submit: errorMessage });
      alert('Error: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Render component form based on type
  const renderComponentForm = () => {
    const props = {
      onSave: handleAddComponent,
      onCancel: () => {
        setShowComponentModal(false);
        setSelectedComponentType('');
        setEditingComponent(null);
      },
      initialData: editingComponent
    };

    switch (selectedComponentType) {
      case 'cruise':
        return <CruiseForm {...props} />;
      case 'flight':
        return <FlightForm {...props} />;
      case 'hotel':
        return <HotelForm {...props} />;
      case 'car':
        return <CarRentalForm {...props} />;
      case 'restaurant':
        return <RestaurantForm {...props} />;
      case 'package':
        return <PackageDealForm {...props} />;
      case 'general':
        return <GeneralItemForm {...props} />;
      case 'insurance':
        return <InsuranceForm {...props} />;
      default:
        return null;
    }
  };

  if (loadingQuote) {
    return (
      <div className="loading-container">
        <div className="loading">Loading quote...</div>
      </div>
    );
  }

  return (
    <div className="quote-builder">
      <div className="quote-builder-header">
        <h1>{isEditMode ? 'Edit Quote' : 'Create New Quote'}</h1>
        <div className="quote-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/agent/quotes')}
          >
            Back to Quotes
          </button>
          {isEditMode ? (
            <button 
              className="btn btn-primary"
              onClick={() => handleSaveQuote(false)}
              disabled={loading}
            >
              Save Changes
            </button>
          ) : (
            <button 
              className="btn btn-secondary"
              onClick={() => handleSaveQuote(false)}
              disabled={loading}
            >
              Save Draft
            </button>
          )}
        </div>
      </div>

      {/* Progress Steps */}
      <div className="progress-steps">
        <div className={`step ${activeStep === 'client' ? 'active' : ''} ${quote.client ? 'completed' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">Select Client</span>
        </div>
        <div className={`step ${activeStep === 'details' ? 'active' : ''} ${quote.travelDates.start ? 'completed' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">Trip Details</span>
        </div>
        <div className={`step ${activeStep === 'components' ? 'active' : ''} ${quote.components.length > 0 ? 'completed' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">Add Components</span>
        </div>
        <div className={`step ${activeStep === 'preview' ? 'active' : ''}`}>
          <span className="step-number">4</span>
          <span className="step-label">Review & Send</span>
        </div>
      </div>

      {/* Step Content */}
      <div className="quote-builder-content">
        {/* Client Selection Step */}
        {activeStep === 'client' && (
          <div className="step-content">
            <h2>Select Client</h2>
            <div className="client-selection">
              <button 
                className="btn btn-primary"
                onClick={() => setShowClientModal(true)}
              >
                Select Existing Client
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowClientModal(true);
                  // Set flag for new client form
                }}
              >
                Create New Client
              </button>
            </div>

            {quote.client && (
              <div className="selected-client">
                <h3>Selected Client:</h3>
                <div className="client-info">
                  <p><strong>{quote.client.firstName} {quote.client.lastName}</strong></p>
                  <p>{quote.client.email}</p>
                  <p>{quote.client.phone}</p>
                </div>
                <button 
                  className="btn btn-primary"
                  onClick={() => setActiveStep('details')}
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        )}

        {/* Trip Details Step */}
        {activeStep === 'details' && (
          <div className="step-content">
            <h2>Trip Details</h2>
            <form className="trip-details-form">
              <div className="form-group">
                <label>Quote Title*</label>
                <input
                  type="text"
                  value={quote.quoteTitle}
                  onChange={(e) => setQuote(prev => ({ ...prev, quoteTitle: e.target.value }))}
                  placeholder="e.g., Smith Family Caribbean Cruise"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Travel Start Date*</label>
                  <input
                    type="date"
                    value={quote.travelDates.start}
                    onChange={(e) => setQuote(prev => ({ 
                      ...prev, 
                      travelDates: { ...prev.travelDates, start: e.target.value }
                    }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Travel End Date*</label>
                  <input
                    type="date"
                    value={quote.travelDates.end}
                    onChange={(e) => setQuote(prev => ({ 
                      ...prev, 
                      travelDates: { ...prev.travelDates, end: e.target.value }
                    }))}
                    min={quote.travelDates.start}
                    required
                  />
                </div>
              </div>

              <div className="travelers-section">
                <h3>Travelers</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Adults*</label>
                    <input
                      type="number"
                      min="1"
                      value={quote.travelers.adults}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        travelers: { ...prev.travelers, adults: parseInt(e.target.value) }
                      }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Children</label>
                    <button 
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setQuote(prev => ({
                          ...prev,
                          travelers: {
                            ...prev.travelers,
                            children: [...prev.travelers.children, { age: 0 }]
                          }
                        }));
                      }}
                    >
                      Add Child
                    </button>
                  </div>
                </div>

                {quote.travelers.children.map((child, index) => (
                  <div key={index} className="child-age">
                    <label>Child {index + 1} Age:</label>
                    <input
                      type="number"
                      min="0"
                      max="17"
                      value={child.age}
                      onChange={(e) => {
                        const newChildren = [...quote.travelers.children];
                        newChildren[index] = { age: parseInt(e.target.value) };
                        setQuote(prev => ({
                          ...prev,
                          travelers: { ...prev.travelers, children: newChildren }
                        }));
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        setQuote(prev => ({
                          ...prev,
                          travelers: {
                            ...prev.travelers,
                            children: prev.travelers.children.filter((_, i) => i !== index)
                          }
                        }));
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="form-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveStep('client')}
                >
                  Back
                </button>
                <button 
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setActiveStep('components')}
                  disabled={!quote.quoteTitle || !quote.travelDates.start || !quote.travelDates.end}
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Components Step */}
        {activeStep === 'components' && (
          <div className="step-content">
            <h2>Travel Components</h2>
            
            <div className="component-types">
              <h3>Add Component:</h3>
              <div className="component-type-grid">
                {componentTypes.map(type => (
                  <button
                    key={type.id}
                    className="component-type-btn"
                    onClick={() => {
                      setSelectedComponentType(type.id);
                      setShowComponentModal(true);
                    }}
                  >
                    <span className="component-icon">{type.icon}</span>
                    <span className="component-name">{type.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {quote.components.length > 0 && (
              <div className="components-list">
                <h3>Added Components:</h3>
                {quote.components.map(component => (
                  <div key={component.id} className="component-card">
                    <div className="component-header">
                      <h4>{componentTypes.find(t => t.id === component.type)?.icon} {component.title}</h4>
                      <div className="component-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setEditingComponent(component);
                            setSelectedComponentType(component.type);
                            setShowComponentModal(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteComponent(component.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="component-details">
                      <p>{component.description}</p>
                      <p className="component-price">${(parseFloat(component.price) || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setActiveStep('details')}
              >
                Back
              </button>
              <button 
                type="button"
                className="btn btn-primary"
                onClick={() => setActiveStep('preview')}
                disabled={quote.components.length === 0}
              >
                Continue to Preview
              </button>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {activeStep === 'preview' && (
          <div className="step-content">
            <h2>Review Quote</h2>
            <div className="quote-preview">
              <div className="preview-header">
                <h3>{quote.quoteTitle}</h3>
                <p className="preview-date">Created: {new Date().toLocaleDateString()}</p>
              </div>

              <div className="preview-client">
                <h4>Client Information</h4>
                <p>{quote.client?.firstName} {quote.client?.lastName}</p>
                <p>{quote.client?.email}</p>
                <p>{quote.client?.phone}</p>
              </div>

              <div className="preview-trip">
                <h4>Trip Details</h4>
                <p>Travel Dates: {quote.travelDates.start} to {quote.travelDates.end}</p>
                <p>Travelers: {quote.travelers.adults} Adults
                  {quote.travelers.children.length > 0 && `, ${quote.travelers.children.length} Children`}
                </p>
              </div>

              <div className="preview-components">
                <h4>Travel Components</h4>
                {quote.components.map(component => (
                  <div key={component.id} className="preview-component">
                    <div className="preview-component-header">
                      <span>{componentTypes.find(t => t.id === component.type)?.icon} {component.title}</span>
                      <span>${(parseFloat(component.price) || 0).toFixed(2)}</span>
                    </div>
                    <p>{component.description}</p>
                  </div>
                ))}
              </div>

              <div className="preview-pricing">
                <div className="pricing-row">
                  <span>Total Price:</span>
                  <span className="price-total">${quote.totalPrice.toFixed(2)}</span>
                </div>
                
                <div className="deposit-section">
                  <h4>Deposit Information</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Deposit Amount</label>
                      <input
                        type="number"
                        min="0"
                        max={quote.totalPrice}
                        value={quote.depositAmount}
                        onChange={(e) => setQuote(prev => ({ 
                          ...prev, 
                          depositAmount: parseFloat(e.target.value) || 0
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Deposit Due Date</label>
                      <input
                        type="date"
                        value={quote.depositDueDate}
                        onChange={(e) => setQuote(prev => ({ 
                          ...prev, 
                          depositDueDate: e.target.value
                        }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="notes-section">
                  <label>Additional Notes</label>
                  <textarea
                    value={quote.notes}
                    onChange={(e) => setQuote(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional information for the client..."
                    rows="4"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveStep('components')}
                >
                  Back
                </button>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    // Open preview in new window
                    const previewWindow = window.open('', '_blank', 'width=800,height=600');
                    const previewContent = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Quote Preview - ${quote.quoteTitle}</title>
                        <style>
                          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
                          h1, h2, h3 { color: #2563eb; }
                          .header { border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px; }
                          .section { margin-bottom: 30px; }
                          .component { background: #f3f4f6; padding: 15px; margin-bottom: 10px; border-radius: 8px; }
                          .price { font-weight: bold; color: #2563eb; }
                          .total { font-size: 1.5em; color: #2563eb; border-top: 2px solid #e5e7eb; padding-top: 20px; }
                        </style>
                      </head>
                      <body>
                        <div class="header">
                          <h1>${quote.quoteTitle}</h1>
                          <p>Prepared for: ${quote.client?.firstName} ${quote.client?.lastName}</p>
                          <p>Date: ${new Date().toLocaleDateString()}</p>
                        </div>
                        <div class="section">
                          <h2>Trip Details</h2>
                          <p><strong>Travel Dates:</strong> ${quote.travelDates.start} to ${quote.travelDates.end}</p>
                          <p><strong>Travelers:</strong> ${quote.travelers.adults} Adults${quote.travelers.children.length > 0 ? `, ${quote.travelers.children.length} Children` : ''}</p>
                        </div>
                        <div class="section">
                          <h2>Travel Components</h2>
                          ${quote.components.map(comp => `
                            <div class="component">
                              <h3>${comp.title}</h3>
                              <p>${comp.description || ''}</p>
                              <p class="price">${(parseFloat(comp.price) || 0).toFixed(2)}</p>
                            </div>
                          `).join('')}
                        </div>
                        <div class="total">
                          <p>Total Price: ${quote.totalPrice.toFixed(2)}</p>
                          ${quote.depositAmount ? `<p>Deposit Required: ${quote.depositAmount.toFixed(2)}</p>` : ''}
                        </div>
                      </body>
                      </html>
                    `;
                    previewWindow.document.write(previewContent);
                    previewWindow.document.close();
                  }}
                >
                  Preview Quote
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleSaveQuote(true)}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Quote to Client'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Component Modal */}
      {showComponentModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingComponent ? 'Edit' : 'Add'} {componentTypes.find(t => t.id === selectedComponentType)?.name}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowComponentModal(false);
                  setSelectedComponentType('');
                  setEditingComponent(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {renderComponentForm()}
            </div>
          </div>
        </div>
      )}

      {/* Client Selection Modal */}
      <ClientModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        clients={clients}
        onSelectClient={handleClientSelect}
        onCreateClient={handleNewClient}
      />

      {/* Error display */}
      {errors.submit && (
        <div className="alert alert-error">
          {errors.submit}
        </div>
      )}
    </div>
  );
};

export default QuoteBuilder;
